import { DraftInterface } from '../../Interfaces/Draft/DraftInterface'
import { OrderDraftInterface } from '../../Interfaces/OrderInterface'
import { Immutable } from '../Immutable'

export const calculateDeposit = (draft: DraftInterface): DraftInterface => {
  if (!draft.order || draft.order.items.length < 1) {
    return draft
  }

  const order = reCalculateOrder(
    draft.order,
    draft.summary.grossTotal,
    draft.summary.taxTotal,
    draft.order.items[0].percentage !== undefined,
    draft.summary.reductionTotal || null,
  )
  return { ...draft, order }
}

export function reCalculateOrder(
  order: OrderDraftInterface,
  grossTotal: number,
  vatTotal: number,
  usePercentages: boolean,
  reductionTotal?: number,
): OrderDraftInterface {
  const newOrder = Immutable.copy(order)
  const grossTotalAfterReduction = reductionTotal ? grossTotal - reductionTotal : grossTotal
  const last = newOrder.items.pop()
  const allButLast = newOrder.items

  allButLast.forEach((item) => {
    if (usePercentages) {
      item.value = floorTwoDecimals(grossTotalAfterReduction * (item.percentage / 100))
    } else {
      delete item.percentage
    }
    if (newOrder.includeVat && usePercentages) {
      item.vat = floorTwoDecimals(vatTotal * (item.percentage / 100))
    } else if (newOrder.includeVat) {
      const percentageOfTotal = grossTotalAfterReduction !== 0 ? item.value / grossTotalAfterReduction : 0
      item.vat = floorTwoDecimals(vatTotal * percentageOfTotal)
    } else {
      item.vat = 0
    }
  })

  if (usePercentages) {
    last.percentage = 100 - allButLast.reduce((prev, s) => prev + s.percentage, 0)
  } else {
    delete last.percentage
  }
  last.vat = roundTwoDecimals(vatTotal - allButLast.reduce((prev, s) => prev + s.vat, 0))
  last.value = roundTwoDecimals(grossTotalAfterReduction - allButLast.reduce((prev, s) => prev + s.value, 0))
  newOrder.items = [...allButLast, last]
  return newOrder
}

export function getDefaultDepositOrder(draft: DraftInterface): OrderDraftInterface {
  const reductionTotal = draft.summary.reductionTotal
  const value = reductionTotal ? (draft.summary.grossTotal - reductionTotal) / 2 : draft.summary.grossTotal / 2
  const vat = draft.summary.taxTotal / 2
  const depositDueDate = draft.fields.issueDate.value || new Date().toISOString()
  const finalDueDate = draft.fields.dueDate.value || new Date().toISOString()
  const order: OrderDraftInterface = {
    items: [
      { value, vat, dueDate: depositDueDate, percentage: 50 },
      {
        value: draft.summary.grossTotal - value,
        vat: draft.summary.taxTotal - vat,
        dueDate: finalDueDate,
        percentage: 50,
      },
    ],
    orderType: 'downPaymentSameInvoice',
    includeVat: true,
  }
  return reCalculateOrder(order, draft.summary.grossTotal, draft.summary.taxTotal, true, reductionTotal)
}

export function getDefaultScheduleOrder(draft: DraftInterface): OrderDraftInterface {
  const reductionTotal = draft.summary.reductionTotal
  const value = reductionTotal ? (draft.summary.grossTotal - reductionTotal) / 3 : draft.summary.grossTotal / 3
  const vat = draft.summary.taxTotal / 3
  const baseDate = new Date(draft.fields.dueDate.value)
  const date1 = baseDate.toISOString()
  baseDate.setMonth(baseDate.getMonth() + 1)
  const date2 = baseDate.toISOString()
  baseDate.setMonth(baseDate.getMonth() + 1)
  const date3 = baseDate.toISOString()
  const order: OrderDraftInterface = {
    items: [
      { value, vat, percentage: 33, dueDate: date1 },
      { value, vat, percentage: 33, dueDate: date2 },
      { value, vat, percentage: 33, dueDate: date3 },
    ],
    includeVat: true,
    orderType: 'schedulesSameInvoice',
  }
  return reCalculateOrder(order, draft.summary.grossTotal, draft.summary.taxTotal, true, reductionTotal)
}

function floorTwoDecimals(value: number): number {
  return Math.floor(value * 100) / 100
}
function roundTwoDecimals(value: number): number {
  return Math.round(value * 100) / 100
}
